import apiClient from './client';
import { buildQueryString, type BaseListParams } from './utils';
import type {
  Partner,
  CreatePartnerRequest,
  UpdatePartnerRequest,
  PartnerDeletionImpact,
  PaginatedResponse,
  PartnerContactWithCohortCount,
  CreatePartnerContactRequest,
  UpdatePartnerContactRequest,
  PartnerContactCohort,
  PartnerContact,
  PartnerCoachListItem,
} from '../../types';

export type PartnerListParams = BaseListParams;

export const partnersApi = {
  /**
   * Get all partners with optional search and pagination
   */
  list: async (params?: PartnerListParams): Promise<PaginatedResponse<Partner>> => {
    const url = `/api/partners${buildQueryString(params)}`;
    return apiClient.getPaginated<Partner>(url);
  },

  /**
   * Get a single partner by ID
   */
  getById: async (id: string): Promise<Partner> => {
    return apiClient.get<Partner>(`/api/partners/${id}`);
  },

  /**
   * Create a new partner
   */
  create: async (data: CreatePartnerRequest): Promise<Partner> => {
    return apiClient.post<Partner>('/api/partners', data);
  },

  /**
   * Update a partner
   */
  update: async (id: string, data: UpdatePartnerRequest): Promise<Partner> => {
    return apiClient.put<Partner>(`/api/partners/${id}`, data);
  },

  /**
   * Delete a partner (soft delete)
   */
  delete: async (id: string): Promise<void> => {
    return apiClient.delete<void>(`/api/partners/${id}`);
  },

  /**
   * Get counts of downstream records that would be orphaned by deleting this partner
   */
  getDeletionImpact: async (id: string): Promise<PartnerDeletionImpact> => {
    return apiClient.get<PartnerDeletionImpact>(`/api/partners/${id}/deletion-impact`);
  },

  /**
   * Check if a partner code is available
   */
  checkCode: async (code: string): Promise<{ available: boolean }> => {
    return apiClient.get<{ available: boolean }>(`/api/partners/check-code/${code}`);
  },

  // ============================================================================
  // Partner Coaches
  // ============================================================================

  /**
   * Get all partner coaches for a partner
   */
  listCoaches: async (partnerId: string, params?: BaseListParams): Promise<PaginatedResponse<PartnerCoachListItem>> => {
    const url = `/api/partners/${partnerId}/coaches${buildQueryString(params)}`;
    return apiClient.getPaginated<PartnerCoachListItem>(url);
  },

  // ============================================================================
  // Partner Contacts
  // ============================================================================

  /**
   * Get all contacts for a partner with cohort counts
   */
  listContacts: async (partnerId: string): Promise<PartnerContactWithCohortCount[]> => {
    return apiClient.get<PartnerContactWithCohortCount[]>(`/api/partners/${partnerId}/contacts`);
  },

  /**
   * Get a single contact by ID
   */
  getContactById: async (partnerId: string, contactId: string): Promise<PartnerContactWithCohortCount> => {
    return apiClient.get<PartnerContactWithCohortCount>(`/api/partners/${partnerId}/contacts/${contactId}`);
  },

  /**
   * Create a new contact for a partner
   */
  createContact: async (partnerId: string, data: CreatePartnerContactRequest): Promise<PartnerContact> => {
    return apiClient.post<PartnerContact>(`/api/partners/${partnerId}/contacts`, data);
  },

  /**
   * Update a contact
   */
  updateContact: async (
    partnerId: string,
    contactId: string,
    data: UpdatePartnerContactRequest,
  ): Promise<PartnerContact> => {
    return apiClient.put<PartnerContact>(`/api/partners/${partnerId}/contacts/${contactId}`, data);
  },

  /**
   * Delete a contact (soft delete)
   */
  deleteContact: async (partnerId: string, contactId: string): Promise<void> => {
    return apiClient.delete<void>(`/api/partners/${partnerId}/contacts/${contactId}`);
  },

  /**
   * Get cohorts where a contact is the primary contact
   */
  getContactCohorts: async (partnerId: string, contactId: string): Promise<PartnerContactCohort[]> => {
    return apiClient.get<PartnerContactCohort[]>(`/api/partners/${partnerId}/contacts/${contactId}/cohorts`);
  },
};

export default partnersApi;
